import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { listDebtors, searchDebtors, DebtorResponse } from '../api/debtorsApi';
import Layout from '../components/Layout';

const STATUS_LABELS: Record<string, string> = {
  active: 'Actief',
  inactive: 'Inactief',
  blocked: 'Geblokkeerd',
};

// Dekking per grade (Trade4Sure 1-10)
const GRADE_DEKKING: Record<number, string> = {
  1: '€ 50K', 2: '€ 50K', 3: '€ 50K', 4: '€ 25K', 5: '€ 10K',
  6: '€ 5K', 7: 'geen', 8: 'geen', 9: 'geen', 10: 'geen',
};

const gradeColor = (grade: number | null): string => {
  if (grade == null) return 'bg-slate-100 text-slate-500';
  if (grade <= 2) return 'bg-emerald-100 text-emerald-800';
  if (grade === 3) return 'bg-mfb-blue/10 text-mfb-blue';
  if (grade === 4) return 'bg-amber-100 text-amber-800';
  if (grade === 5) return 'bg-orange-100 text-orange-800';
  if (grade === 6) return 'bg-red-100 text-red-800';
  return 'bg-red-200 text-red-900'; // 7-10 geen dekking
};

export const DebtorsPage: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();

  const [debtors, setDebtors] = useState<DebtorResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      navigate('/login');
      return;
    }
    loadDebtors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, accessToken, navigate]);

  const loadDebtors = async () => {
    setLoading(true);
    try {
      const data = await listDebtors(accessToken!);
      setDebtors(data.debtors);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Debiteuren laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      loadDebtors();
      return;
    }
    setLoading(true);
    try {
      const data = await searchDebtors(accessToken!, searchQuery);
      setDebtors(data.debtors);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Zoeken mislukt');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      active: 'bg-emerald-100 text-emerald-700',
      inactive: 'bg-slate-100 text-slate-600',
      blocked: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="font-display text-2xl font-semibold text-mfb-blue">Debiteuren</h1>
        <p className="text-sm text-slate-500">Uw klanten en hun kredietwaardigheid</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-8">
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Zoek op bedrijfsnaam of KvK-nummer..."
            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-mfb-blue outline-none"
          />
          <button type="submit"
            className="px-6 py-2 bg-mfb-orange hover:bg-mfb-orange-dark text-white rounded-lg font-semibold transition shadow-sm">
            Zoeken
          </button>
          {searchQuery && (
            <button type="button" onClick={() => { setSearchQuery(''); loadDebtors(); }}
              className="px-6 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-medium transition">
              Wissen
            </button>
          )}
        </form>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Debiteuren laden...</div>
      ) : debtors.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500">
          Geen debiteuren gevonden.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Bedrijf</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">KvK</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Land</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Grade</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Max dekking</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Laatste toetsing</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {debtors.map((debtor) => (
                  <tr
                    key={debtor.id}
                    onClick={() => navigate(`/debtors/${debtor.id}`)}
                    className="hover:bg-slate-50 cursor-pointer"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-mfb-blue">
                      {debtor.company_name}
                      <span className="text-mfb-orange ml-2">→</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{debtor.company_number || '—'}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{debtor.country_code}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${gradeColor(debtor.grade)}`}>
                        {debtor.grade ?? '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {debtor.dekking != null
                        ? new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(debtor.dekking)
                        : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(debtor.status)}`}>
                        {STATUS_LABELS[debtor.status] || debtor.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {debtor.last_credit_check
                        ? new Date(debtor.last_credit_check).toLocaleDateString('nl-NL')
                        : 'Nooit'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Grade legend */}
      <div className="mt-8 bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-display text-lg font-semibold text-mfb-blue mb-4">Dekking per credit grade (1–10)</h3>
        <div className="grid grid-cols-5 md:grid-cols-10 gap-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((grade) => (
            <div key={grade} className="text-center">
              <div className={`inline-block w-9 h-9 leading-9 rounded-lg font-bold ${gradeColor(grade)}`}>{grade}</div>
              <p className="text-xs text-slate-600 mt-2">{GRADE_DEKKING[grade]}</p>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default DebtorsPage;
